import React, { useMemo, useState } from 'react';
import moment from 'moment';

import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Box from '@mui/material/Box';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers';

import HtoSummary from '../../step4/hto-flow/HtoSummary';
import DfoSummary from '../../step4/dfo-flow/DfoSummary';
import HardwareList from '../../HardwareList';
import CaseAccordion from '../../CaseAccordion';
import NoAccess from '../../NoAccess';
import Input from '../../../../../shared/Input';
import Button from '../../../../../shared/Button';
import Dropzone from '../../../../../shared/dropzone/Dropzone';

export const OsteotomyStep5 = (props) => {
  const {
    classes,
    onClose,
    activeCase,
    updateCase,
    products,
    preopPlan,
    // preopRejected,
    // manufacturingFiles,
    manufacturingFilesRejected,
    caseHardware,
    caseManufacturing,
    fileStatuses,
    fileTypes,
    statuses,
    procedureFlows,
    caseNotifications,
    userId,
  } = props;

  const { alignmentData = {} } = activeCase;

  const [approval, setApproval] = useState('approve');
  const [notes, setNotes] = useState('');
  const [preop, setPreop] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [hardwareList, setHardwareList] = useState(caseHardware || []);
  const [manufacturingList, setManufacturingList] = useState(caseManufacturing || []);

  const initialTibialWidth = useMemo(() => alignmentData?.['%WBL'], [alignmentData]);
  const tibialWidth = useMemo(() => alignmentData?.Tibial_Width, [alignmentData]);
  const RCA = useMemo(() => alignmentData?.['R_CA'], [alignmentData]);
  const DCA = useMemo(() => alignmentData?.['D_CA'], [alignmentData]);
  const initialPTS = useMemo(() => alignmentData?.['Pre_TS'], [alignmentData]);
  const PTS = useMemo(() => alignmentData?.['Post_TS'], [alignmentData]);

  const requiredDate = activeCase?.date
    ? moment(activeCase.date).subtract(7, 'days')
    : moment();

  const [date, setDate] = useState(requiredDate);

  /* -------------------- Upload helpers -------------------- */

  const handleBlocksUpload = (files) => {
    const renamed = files.map(
      (file) => new File([file], `b-${file.name}`, { type: file.type })
    );

    setBlocks([
      ...blocks,
      ...renamed.filter((f) => !blocks.map((b) => b.name).includes(f.name)),
    ]);
  };

  const handleRemovePreop = (name) => {
    setPreop(preop.filter((f) => f.name !== name));
  };

  const handleRemoveBlock = (name) => {
    setBlocks(blocks.filter((f) => f.name !== name));
  };

  /* -------------------- Hardware -------------------- */

  const updateListItem = (list, setList, id, key, value) => {
    const copy = [...list];
    const item = copy.find((i) => i.id === id);
    item[key] = value;
    setList(copy);
  };

  const addToList = (list, setList, product) => {
    const copy = [...list];
    const existing = copy.find(
      (i) => i.product === product.product && i.category === product.category
    );

    if (existing) {
      existing.amount += Number(product.amount);
      setList(copy);
    } else {
      setList([...list, product]);
    }
  };

  const handleSubmit = () => {
    const now = moment().format();

    if (approval === 'reject' && !notes.length) return;

    if (approval === 'approve') {
      const activity = [
        { ...caseNotifications[5].success, date: now, userId },
        { ...caseNotifications[5].file, date: now, userId },
        ...activeCase.activity,
      ];

      const files = [...activeCase.files];
      const newFiles = [];

      const preopFile = preop[0];
      if (preopFile) {
        files.unshift({
          id: `${activeCase.id}_${fileTypes.preop}_${preopFile.name}`,
          name: preopFile.name,
          type: fileTypes.preop,
          status: fileStatuses.PENDING,
          date: now,
          userId,
        });
        newFiles.push(preopFile);
      }

      blocks.forEach((file) => {
        files.unshift({
          id: `${activeCase.id}_${fileTypes.manufacturing}_${file.name}`,
          name: file.name,
          type: fileTypes.manufacturing,
          date: now,
          userId,
        });
        newFiles.push(file);
      });

      const approvedPreop = files.find(
        (f) => f.type === fileTypes.preop && f.status === fileStatuses.APPROVED
      );

      updateCase(
        {
          ...activeCase,
          step: approvedPreop ? 7 : 6,
          activity,
          hardware: hardwareList,
          manufacturing: manufacturingList,
          alignmentData: { ...alignmentData, wedgeApproved: true },
          files,
          completionDates: { ...activeCase.completionDates, step5: now },
          completedBy: { ...activeCase.completedBy, step5: userId },
          status: statuses.inProgress,
          notes: { ...activeCase.notes, step5: notes },
          requiredDate: date.format(),
        },
        newFiles
      ).then(onClose);
    } else {
      updateCase(
        {
          ...activeCase,
          step: 4,
          activity: [
            { ...caseNotifications[5].reject, date: now, userId },
            ...activeCase.activity,
          ],
          alignmentData: {
            ...alignmentData,
            measurementsRejected: true,
          },
          notes: { ...activeCase.notes, step5: notes },
          status: statuses.rejected,
        },
        null,
        true
      ).then(onClose);
    }
  };

  const renderSummaryView = () => {
    if (
      activeCase.procedureFlow === procedureFlows.HTO ||
      activeCase.procedureFlow === procedureFlows.HTO_LATERAL
    ) {
      return (
        <HtoSummary
          classes={classes}
          {...alignmentData}
          HKA={alignmentData['Pre-Op_HKA']}
          tibialWidth={tibialWidth}
          initialTibialWidth={initialTibialWidth}
          RCA={RCA}
          DCA={DCA}
          initialPTS={initialPTS}
          PTS={PTS}
        />
      );
    }

    if (
      activeCase.procedureFlow === procedureFlows.DFO_Lateral ||
      activeCase.procedureFlow === procedureFlows.DFO_Medial
    ) {
      return (
        <DfoSummary
          classes={classes}
          flow={activeCase.procedureFlow}
          {...alignmentData}
          HKA={alignmentData['Pre-Op_HKA']}
          tibialWidth={tibialWidth}
          initialTibialWidth={initialTibialWidth}
          RCA={RCA}
          DCA={DCA}
          initialPTS={initialPTS}
          PTS={PTS}
        />
      );
    }

    return null;
  };

  const rejected = approval === 'reject';

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="case-step-container">
        <div className="case-step-content">
          <div className="case-step-row">
            <div className="column">
              <div className="modal-item-circle">5</div>
            </div>

            <div className="modal-item-content">
              <div className="m-b-lg">Review Measurements</div>

              {activeCase.access ? (
                <>
                  {renderSummaryView()}

                  <div className="radio-group-container d-flex flex-end">
                    <RadioGroup row value={approval} onChange={(e) => setApproval(e.target.value)}>
                      <FormControlLabel value="approve" control={<Radio />} label="Approve" />
                      <FormControlLabel value="reject" control={<Radio />} label="Reject" />
                    </RadioGroup>
                  </div>

                  {rejected && (
                    <Input
                      fullWidth
                      multiline
                      label="Notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  )}

                 <Box sx={{ mt: 1 }}>
                   <Dropzone
                     files={preop}
                     onUpload={setPreop}
                     type="pdf"
                     multiple={false}
                     disabled={manufacturingFilesRejected}
                     onRemove={handleRemovePreop}
                   />
                 </Box>

                  <Box sx={{ mt: 2 }}>
                    <Dropzone
                      files={blocks}
                      onUpload={handleBlocksUpload}
                      icon="block"
                      type="zip"
                      onRemove={handleRemoveBlock}
                    />
                  </Box>

                  <HardwareList
                    classes={classes}
                    hardwareList={hardwareList}
                    products={products}
                    onAdd={(p) => addToList(hardwareList, setHardwareList, p)}
                    onRemove={(id) =>
                      setHardwareList(hardwareList.filter((i) => i.id !== id))
                    }
                    onAmountChange={(id, v) =>
                      updateListItem(hardwareList, setHardwareList, id, 'amount', v)
                    }
                    onCategoryChange={(id, v) =>
                      updateListItem(hardwareList, setHardwareList, id, 'category', v)
                    }
                    onChange={(id, v) =>
                      updateListItem(hardwareList, setHardwareList, id, 'product', v)
                    }
                    title="Proposed Hardware"
                    procedureFlow={activeCase.procedureFlow}
                  />

                  <HardwareList
                    classes={classes}
                    hardwareList={manufacturingList}
                    products={products}
                    onAdd={(p) => addToList(manufacturingList, setManufacturingList, p)}
                    onRemove={(id) =>
                      setManufacturingList(manufacturingList.filter((i) => i.id !== id))
                    }
                    onAmountChange={(id, v) =>
                      updateListItem(manufacturingList, setManufacturingList, id, 'amount', v)
                    }
                    onCategoryChange={(id, v) =>
                      updateListItem(manufacturingList, setManufacturingList, id, 'category', v)
                    }
                    onChange={(id, v) =>
                      updateListItem(manufacturingList, setManufacturingList, id, 'product', v)
                    }
                    title="PSI Manufacturing"
                    procedureFlow={activeCase.procedureFlow}
                  />

                  <div className="hardware-list-container">
                    <div className="hardware-list-header">
                      <div className="secondary font-size-sm">REQUIRED DATE</div>
                    </div>

                    <DatePicker
                      desktopModeMediaQuery="(min-width: 0px)"
                      sx={{ mt: 1 }}
                      value={date}
                      onChange={(d) => setDate(d)}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          variant: 'standard',
                        },
                      }}
                    />
                  </div>
                </>
              ) : (
                <NoAccess />
              )}
            </div>
          </div>

          <CaseAccordion currentStep={5} />
        </div>

        <div className="case-modal-actions">
          <div className="secondary pointer" onClick={onClose}>
            Close
          </div>
          <Button
            text="Submit"
            color="primary"
            onClick={handleSubmit}
            disabled={
              (approval === 'approve' &&
                (!preopPlan && !preop.length)) ||
              (approval === 'reject' && !notes.length)
            }
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};
